import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useTheme } from '@mui/material/styles';

import { Question } from '../../../types';

type TriviaResponseCardProps = {
  backCoverTextButton?: string;
  givenAnswers: number[];
  questions: Question[];
  onClickOk: () => void;
};

const keyPrefix = 'components.incentives.TriviaResponseCard';

export const TriviaResponseCard: FC<TriviaResponseCardProps> = ({
  backCoverTextButton,
  givenAnswers,
  questions,
  onClickOk,
}): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix });

  return (
    <Card sx={{ width: 674, minHeight: 652 }}>
      <Stack justifyContent='space-between' height='100%' pb={7}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant='h5' py={2}>
              {t('title')}
            </Typography>
            <Stack gap={5}>
              {questions.map(({ orden, question, answers }, questionId) => {
                const selectedAnswerId = givenAnswers[questionId];

                // Como la app: la correcta y, si se falló, la elegida, en el
                // orden en que aparecen las opciones.
                return (
                  <Stack gap={3} key={questionId}>
                    <Typography fontSize='24px'>{`${orden}. ${question}`}</Typography>
                    {answers.map(({ answer, correct }, answerId) =>
                      correct || answerId === selectedAnswerId ? (
                        <GivenAnswer key={answerId} answer={answer} correct={correct} />
                      ) : null,
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </CardContent>
        <Box px={4} pt={4}>
          <Button variant='contained' fullWidth onClick={onClickOk}>
            {backCoverTextButton && backCoverTextButton !== 'null'
              ? backCoverTextButton
              : t('ok')}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};

type GivenAnswerProps = {
  answer: string;
  correct: boolean;
};

const GivenAnswer: FC<GivenAnswerProps> = ({
  answer,
  correct,
}): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('translation', { keyPrefix });
  return (
    // Alto mínimo y no fijo: con `height: 70px` una respuesta de varias líneas
    // quedaba recortada arriba y abajo (VWA-34, como VAM-9 en la app).
    // El nombre distingue la correcta de la elegida para quien no ve el color.
    <Card
      role='group'
      aria-label={t(correct ? 'correctAnswer' : 'givenAnswer')}
      sx={{
        minHeight: '70px',
        borderRadius: '7px',
        display: 'flex',
        alignItems: 'center',
        border: `3px solid ${correct ? theme.palette.success.main : 'red'}`,
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        width='100%'
        px='32px'
        py={2}
        sx={{ color: correct ? theme.palette.success.main : 'red' }}
      >
        {correct ? (
          <CheckCircleOutlineIcon titleAccess={t('correct')} sx={{ flexShrink: 0 }} />
        ) : (
          <HighlightOffIcon titleAccess={t('incorrect')} sx={{ flexShrink: 0 }} />
        )}
        <Typography textAlign='left' fontWeight='bold' color='inherit'>
          {answer}
        </Typography>
      </Stack>
    </Card>
  );
};
